import * as Yup from 'yup'
import {
    alphaWithSpaceRegex,
    emailRegex,
    emailSpclChrRegex,
    isNumberRegex,
    namePattern,
    passwordPattern,
    specialCharacterRegex,
    alphaWithSpaceandNumberRegex
} from './validationHelpers'
/**
 * Validation schema for user signup form.
 * Ensures required fields, character limits, regex-based validation, and password complexity.
 */
const SIGNUP_SCHEMA = Yup.object().shape({
    Username: Yup.string()
        .required('Username is required.')
        .matches(namePattern, 'Username is invalid. Please enter the username with alphabets only. Eg: JohnDoe')
        .min(6, 'Username should be between 6 to 30 characters.')
        .max(30, 'Username should be between 6 to 30 characters.'),
    given_name: Yup.string()
        .required('First Name is required.')
        .max(40, 'First Name should be less than or equal to 40 characters.')
        .matches(namePattern, 'First Name is invalid. Please enter the first name with alphabets only. Eg: John'),
    family_name: Yup.string()
        .required('Last Name is required.')
        .max(80, 'Last Name should be less than or equal to 80 characters.')
        .matches(namePattern, 'Last Name is invalid. Please enter the last name with alphabets only. Eg: Doe'),
    email: Yup.string()
        .trim()
        .required('Email Address is required.')
        .test(
            'matches-email-pattern',
            'Email Address format is invalid. Alphabets (upper case and lower case), Numbers and Symbols like asterisk “@”, Hyphen (-), dot (.) and Underscore (_) are allowed.',
            (value) => {
                // Check if the value matches either regex pattern
                return emailRegex.test(value) && emailSpclChrRegex.test(value)
            }
        )
        .min(6, 'Email Address should be between 6 to 80 characters.')
        .max(80, 'Email Address should be between 6 to 80 characters.'),
    Password: Yup.string()
        .required('Password is required.')
        .min(
            8,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .max(
            99,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .matches(
            passwordPattern,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .matches(
            /^\S*$/,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        ),

    ConfirmPassword: Yup.string()
        .required('Confirm Password is required.')
        .oneOf([Yup.ref('Password')], 'Passwords do not match.'),
    country: Yup.string().required('Country is required.'),
    state: Yup.string().required('State/Province is required.'),
    address1: Yup.string()
        .required('Street Address is required.')
        .min(4, 'Street Address should be between 4 to 255 characters.')
        .max(255, 'Street Address should be between 4 to 255 characters.'),
    city: Yup.string()
        .required('City is required.')
        .min(2, 'City should be between 2 to 40 characters.')
        .max(40, 'City should be between 2 to 40 characters.')
        .matches(alphaWithSpaceRegex, 'City is invalid. Please enter the city with alphabets only (space is allowed).'),
    pinCode: Yup.string()
        .required('Postal Code is required.')
        .matches(specialCharacterRegex, 'Postal Code is invalid. Please enter the postal code without symbols.')
        .min(4, 'Postal Code should be between 4 to 20 characters.')
        .max(20, 'Postal Code should be between 4 to 20 characters.'),
    countryCode: Yup.string().test('isRequired', 'Please select your country code.', function (value) {
        // Check if countryCode is required based on the presence of phoneNumber
        const phoneNumber = this.parent.phoneNumber
        return phoneNumber && phoneNumber.length > 0 ? !!value : true
    }),
    phoneNumber: Yup.string()
        .test('isRequired', 'Telephone is required.', function (value) {
            // Check if phoneNumber is required based on the presence of countryCode
            const countryCode = this.parent.countryCode
            return countryCode && countryCode.length > 0 ? !!value : true
        })
        .matches(isNumberRegex, 'Telephone is invalid. Please enter the telephone with numbers only (space is allowed).')
        .min(6, 'Telephone should be between 6 to 40 characters.')
        .max(38, 'Telephone should be between 6 to 40 characters.'),
    accountName: Yup.string()
        .required('Company/Institution Name is required.')
        .matches(
            /^(?=.*\S).*$/,
            'Company/Institution Name is invalid. Please enter the company/institution name without symbols (period (.), hyphen (-), and space between the characters is allowed).'
        )
        .matches(
            alphaWithSpaceandNumberRegex,
            'Company/Institution Name is invalid. Please enter the company/institution name without symbols (period (.), hyphen (-), and space between the characters is allowed).'
        )
        .max(255, 'Company/Institution Name should be less than or equal to 255 characters.'),
    subscribeToEmail: Yup.boolean()
        .oneOf([true, false], 'Select the Subscribe to company and product related emails checkbox.')
        .required('Select the Subscribe to company and product related emails checkbox.'),
    isStudent: Yup.boolean().oneOf([true, false], 'Select the checkbox.').required('Select the checkbox.')
})
const SIGNUP_SCHEMA_WITHOUT_STATES = Yup.object().shape({
    Username: Yup.string()
        .required('Username is required.')
        .matches(namePattern, 'Username is invalid. Please enter the username with alphabets only. Eg: JohnDoe')
        .min(6, 'Username should be between 6 to 30 characters.')
        .max(30, 'Username should be between 6 to 30 characters.'),
    given_name: Yup.string()
        .required('First Name is required.')
        .max(40, 'First Name should be less than or equal to 40 characters.')
        .matches(namePattern, 'First Name is invalid. Please enter the first name with alphabets only. Eg: John'),
    family_name: Yup.string()
        .required('Last Name is required.')
        .max(80, 'Last Name should be less than or equal to 80 characters.')
        .matches(namePattern, 'Last Name is invalid. Please enter the last name with alphabets only. Eg: Doe'),
    email: Yup.string()
        .trim()
        .required('Email Address is required.')
        .test(
            'matches-email-pattern',
            'Email Address format is invalid. Alphabets (upper case and lower case), Numbers and Symbols like asterisk “@”, Hyphen (-), dot (.) and Underscore (_) are allowed.',
            (value) => {
                // Check if the value matches either regex pattern
                return emailRegex.test(value) && emailSpclChrRegex.test(value)
            }
        )
        .min(6, 'Email Address should be between 6 to 80 characters.')
        .max(80, 'Email Address should be between 6 to 80 characters.'),
    Password: Yup.string()
        .required('Password is required.')
        .min(
            8,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .max(
            99,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .matches(
            passwordPattern,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .matches(
            /^\S*$/,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        ),

    ConfirmPassword: Yup.string()
        .required('Confirm Password is required.')
        .oneOf([Yup.ref('Password')], 'Passwords do not match.'),
    country: Yup.string().required('Country is required.'),
    state: Yup.string(),
    address1: Yup.string()
        .required('Street Address is required.')
        .min(4, 'Street Address should be between 4 to 255 characters.')
        .max(255, 'Street Address should be between 4 to 255 characters.'),
    city: Yup.string()
        .required('City is required.')
        .min(2, 'City should be between 2 to 40 characters.')
        .max(40, 'City should be between 2 to 40 characters.')
        .matches(alphaWithSpaceRegex, 'City is invalid. Please enter the city with alphabets only (space is allowed).'),
    pinCode: Yup.string()
        .required('Postal Code is required.')
        .matches(specialCharacterRegex, 'Postal Code is invalid. Please enter the postal code without symbols.')
        .min(4, 'Postal Code should be between 4 to 20 characters.')
        .max(20, 'Postal Code should be between 4 to 20 characters.'),
    countryCode: Yup.string().test('isRequired', 'Please select your country code.', function (value) {
        // Check if countryCode is required based on the presence of phoneNumber
        const phoneNumber = this.parent.phoneNumber
        return phoneNumber && phoneNumber.length > 0 ? !!value : true
    }),
    phoneNumber: Yup.string()
        .test('isRequired', 'Telephone is required.', function (value) {
            // Check if phoneNumber is required based on the presence of countryCode
            const countryCode = this.parent.countryCode
            return countryCode && countryCode.length > 0 ? !!value : true
        })
        .matches(isNumberRegex, 'Telephone is invalid. Please enter the telephone with numbers only (space is allowed).')
        .min(6, 'Telephone should be between 6 to 40 characters.')
        .max(38, 'Telephone should be between 6 to 40 characters.'),
    accountName: Yup.string()
        .required('Company/Institution Name is required.')
        .matches(
            /^(?=.*\S).*$/,
            'Company/Institution Name is invalid. Please enter the company/institution name without symbols (period (.), hyphen (-), and space between the characters is allowed).'
        )
        .matches(
            alphaWithSpaceandNumberRegex,
            'Company/Institution Name is invalid. Please enter the company/institution name without symbols (period (.), hyphen (-), and space between the characters is allowed).'
        )
        .max(255, 'Company/Institution Name should be less than or equal to 255 characters.'),
    subscribeToEmail: Yup.boolean()
        .oneOf([true, false], 'Select the Subscribe to company and product related emails checkbox.')
        .required('Select the Subscribe to company and product related emails checkbox.'),
    isStudent: Yup.boolean().oneOf([true, false], 'Select the checkbox.').required('Select the checkbox.')
})
/**
 * Validation schema for user login form.
 */
const LOGIN_SCHEMA = Yup.object().shape({
    Username: Yup.string()
        .required('Username/Email Address is required.')
        .test('username-email-validation', 'Invalid Username or Email', (value) => {
            if (!/^[a-zA-Z]+$/.test(value)) {
                // If it doesn't match the username pattern, validate as an email
                return Yup.string()
                    .email('Invalid email address')
                    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email address')
                    .required('Username/Email Address is required')
                    .isValidSync(value)
            } else {
                // If it matches the username pattern, validate as a username
                return /^[a-zA-Z]+$/.test(value)
            }
        }),
    Password: Yup.string().required('Password is required.'),
    rememberMe: Yup.boolean().optional()
})
/**
 * Validation schema for verifying user with a code.
 */
const VERIFY_USER_SCHEMA = Yup.object().shape({
    code: Yup.number().required('Code is required.')
})
/**
 * Validation schema for forgot password form.
 */
const FORGOT_PASSWORD_SCHEMA = Yup.object().shape({
    Username: Yup.string()
        .required('Username/Email Address is required.')
        .test('username-email-validation', 'Username/Email Address is invalid.', (value) => {
            if (!/^[a-zA-Z]+$/.test(value)) {
                // If it doesn't match the username pattern, validate as an email
                return Yup.string()
                    .email('Invalid email address')
                    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Username/Email Address is invalid.')
                    .required('Username/Email Address is invalid.')
                    .isValidSync(value)
            } else {
                // If it matches the username pattern, validate as a username
                return /^[a-zA-Z0-9._-]+$/.test(value)
            }
        })
})
/**
 * Validation schema for verifying and resetting password.
 */
const VERIFY_FORGOT_PASSWORD_SCHEMA = Yup.object().shape({
    verificationCode: Yup.number().required('Code is required.'),
    newPassword: Yup.string()
        .required('New Password is required.')
        .min(
            8,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .max(
            99,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .matches(
            passwordPattern,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .matches(
            /^\S*$/,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        ),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords do not match.')
        .required('Confirm Password is required.')
})
/**
 * Validation schema for changing password.
 */
const CHANGE_PASSWORD_SCHEMA = Yup.object().shape({
    oldPassword: Yup.string().required('Old Password is required.'),
    newPassword: Yup.string()
        .required('New Password is required.')
        .min(
            8,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .max(
            99,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .matches(
            passwordPattern,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        )
        .matches(
            /^\S*$/,
            'Password needs to contain at least: 8 characters but less than 99 characters, 1 number, 1 uppercase and 1 lowercase letter, and one of the following special characters !@#$%^&*.'
        ),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords do not match.')
        .required('Confirm Password is required')
})
/**
 * Validation schema for forgot username or email form.
 */
const FORGOT_USERNAME_OR_EMAIL_SCHEMA = Yup.object().shape({
    forgotValue: Yup.string().required('Please select an option from the list.'),
    Username: Yup.string()
        .test('username-required', 'Username is required.', function (value) {
            return this.parent.forgotValue === 'email' ? !!value : true
        })
        .matches(namePattern, 'Username is invalid.'),
    email: Yup.string()
        .test('email-required', 'Email Address is required.', function (value) {
            return this.parent.forgotValue === 'username' ? !!value : true
        })
        .trim()
        .test('matches-email-pattern', 'Email Address is invalid.', function (value = '') {
            return this.parent.forgotValue === 'username' ? emailRegex.test(value) && emailSpclChrRegex.test(value) : true
        })
})

export {
    SIGNUP_SCHEMA,
    LOGIN_SCHEMA,
    VERIFY_USER_SCHEMA,
    FORGOT_PASSWORD_SCHEMA,
    VERIFY_FORGOT_PASSWORD_SCHEMA,
    CHANGE_PASSWORD_SCHEMA,
    FORGOT_USERNAME_OR_EMAIL_SCHEMA,
    SIGNUP_SCHEMA_WITHOUT_STATES
}
